import type { GeoMapMarker } from '@/shared/ui/map';
import { estimateTravelBetween, roadDistanceKm, type GeoPoint } from '../routeEstimate';
import type { StationChainStopValue } from '../stationChainFormSchema';

/** Efektivní souřadnice zastávky: vybrané nástupní místo, jinak hlavní poloha stanice. */
export function stopCoord(stop: StationChainStopValue): GeoPoint | null {
  if (stop.stationDetailId != null) {
    const point = stop.points.find((p) => p.id === stop.stationDetailId);
    if (point && point.lat != null && point.lng != null) {
      return { lat: point.lat, lng: point.lng };
    }
  }
  if (stop.stationLat != null && stop.stationLng != null) {
    return { lat: stop.stationLat, lng: stop.stationLng };
  }
  return null;
}

/** Přepočítá `travel` u zastávek s `travelAuto=true` (1. zastávka vždy 0). */
export function recomputeTravel(stops: StationChainStopValue[]): StationChainStopValue[] {
  return stops.map((stop, index) => {
    if (index === 0) {
      return stop.travel === 0 ? stop : { ...stop, travel: 0 };
    }
    if (!stop.travelAuto) {
      return stop;
    }
    const prev = stopCoord(stops[index - 1]);
    const current = stopCoord(stop);
    const travel = prev && current ? estimateTravelBetween(prev, current) : 0;
    return travel === stop.travel ? stop : { ...stop, travel };
  });
}

/** Souhrnná délka trasy (km, zaokrouhleno na 1 desetinné místo) z efektivních souřadnic zastávek. */
export function totalDistanceKm(stops: StationChainStopValue[]): number {
  let total = 0;
  let previous: GeoPoint | null = null;
  for (const stop of stops) {
    const coord = stopCoord(stop);
    if (coord) {
      if (previous) {
        total += roadDistanceKm(previous, coord);
      }
      previous = coord;
    }
  }
  return Math.round(total * 10) / 10;
}

/** Celková jízdní doba (min) = Σ (dwell + travel). */
export function totalDurationMin(stops: StationChainStopValue[]): number {
  return stops.reduce((sum, stop) => sum + stop.dwell + stop.travel, 0);
}

/** „2 h 15 min" / „45 min" z celkového počtu minut. */
export function formatDuration(min: number): string {
  const hours = Math.floor(min / 60);
  const minutes = min % 60;
  return hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;
}

function tooltip(stop: StationChainStopValue): string {
  const point =
    stop.stationDetailId != null ? stop.points.find((p) => p.id === stop.stationDetailId) : null;
  return point ? `${stop.stationName} · ${point.name}` : stop.stationName;
}

/** Markery mapy v barvě trasy, číslované v pořadí; tooltip = název stanice (+ nástupní místo). */
export function buildChainMarkers(stops: StationChainStopValue[], color: string): GeoMapMarker[] {
  return stops
    .map((stop, index) => ({ stop, index, coord: stopCoord(stop) }))
    .filter((entry) => entry.coord != null)
    .map((entry) => ({
      key: entry.stop.key,
      lat: entry.coord!.lat,
      lng: entry.coord!.lng,
      label: String(entry.index + 1),
      color,
      tooltip: tooltip(entry.stop),
    }));
}
